<script lang="ts">
	import DeliveryCountrySelect from "@components/DeliveryCountrySelect.svelte";
	import Logo from "@components/Logo.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { setCookie } from "@utils/setCookie.js";
	import chevronIcon from "@core/assets/icons/chevron.svg?raw";
	import facebookIcon from "@core/assets/icons/facebook.svg?raw";
	import instagramIcon from "@core/assets/icons/instagram.svg?raw";
	import youtubeIcon from "@core/assets/icons/youtube.svg?raw";
	import fastestLogo from "@core/assets/images/fastest-logo.svg?raw";
	import type { Country } from "@core/schema/Country.js";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { fathersContact } from "@core/schema/fathersCompany.js";
	import { fathersPlaces } from "@core/schema/fathersPlaces.js";
	import { getAboutUsPath } from "@core/schema/paths/getAboutUsPath.js";
	import { getContactPath } from "@core/schema/paths/getContactPath.js";
	import { getCookiesPolicyPath } from "@core/schema/paths/getCookiesPolicyPath.js";
	import { getCoursesListingPath } from "@core/schema/paths/getCoursesListingPath.js";
	import { getDeliveryAndPaymentPath } from "@core/schema/paths/getDeliveryAndPaymentPath.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getGiftSubscriptionPath } from "@core/schema/paths/getGiftSubscriptionPath.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { getMembershipPagePath } from "@core/schema/paths/getMembershipPagePath.js";
	import { getNewsAnchor } from "@core/schema/paths/getNewsAnchor.js";
	import { getPostsListingPath } from "@core/schema/paths/getPostsListingPath.js";
	import { getSubcategoryPath } from "@core/schema/paths/getSubcategoryPath.js";
	import { getSubscriptionPagePath } from "@core/schema/paths/getSubscriptionPagePath.js";
	import { getTermsOfServicePath } from "@core/schema/paths/getTermsOfServicePath.js";
	import { getWholesalePath } from "@core/schema/paths/getWholesalePath.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { formatPhoneNumber } from "@core/utils/formatPhoneNumber.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { defaultDestinationCountry } from "../../defaultDestinationCountry.js";
	import { destinationCountryCookieName } from "../../destinationCountryCookieName.js";
	import { selectedDestinationCountry } from "../../state/selectedDestinationCountry.svelte.js";
	import CurrencySwitcher from "./CurrencySwitcher.svelte";
	import translations from "./Footer.translations.json" with { type: "json" };
	import LanguageSwitcher from "./LanguageSwitcher.svelte";

	const translate = getTranslate(translations);

	const { tenant, subcategories } = getGlobalTemplateProps();
	type FooterMenu = { link: string; label: LocalizedString }[];

	const {
		pageHasMobileBottomBar = false,
	}: {
		pageHasMobileBottomBar?: boolean;
	} = $props();

	const footerLinks: FooterMenu = [
		{
			link: getTermsOfServicePath(tenant),
			label: { [Locale.cs]: "Obchodní podmínky", [Locale.en]: "Terms of Service" },
		},
		{
			link: getDeliveryAndPaymentPath(tenant),
			label: { [Locale.cs]: "Doprava a platba", [Locale.en]: "Delivery & Payment" },
		},
		{ link: getCookiesPolicyPath(tenant), label: { [Locale.cs]: "Cookies zásady", [Locale.en]: "Cookies Policy" } },
		{ link: getGdprPath(tenant), label: { [Locale.cs]: "Ochrana osobních údajů", [Locale.en]: "Data Protection" } },
	];

	const footerMenu: { title: string; links: FooterMenu }[] = [
		{
			title: "E-shop",
			links: [
				{
					link: getHomepagePath(tenant) + getNewsAnchor(tenant.locale),
					label: { [Locale.cs]: "Novinky", [Locale.en]: "News" },
				},
				...subcategories.slice(0, 4).map((subcategory) => ({
					link: getSubcategoryPath(tenant.locale, subcategory),
					label: subcategory.name,
				})),
			],
		},
		{
			title: "Father’s",
			links: [
				{ link: getSubscriptionPagePath(tenant), label: { [Locale.cs]: "Předplatné", [Locale.en]: "Subscription" } },
				{
					link: getGiftSubscriptionPath(tenant),
					label: { [Locale.cs]: "Darujte předplatné", [Locale.en]: "Gift a Subscription" },
				},
				{ link: getMembershipPagePath(tenant), label: { [Locale.cs]: "Členství", [Locale.en]: "Membership" } },
				{ link: getCoursesListingPath(tenant), label: { [Locale.cs]: "Kurzy", [Locale.en]: "Courses" } },
				{ link: getWholesalePath(tenant), label: { [Locale.cs]: "B2B", [Locale.en]: "B2B" } },
				{ link: getAboutUsPath(tenant), label: { [Locale.cs]: "O nás", [Locale.en]: "About Us" } },
				{ link: getPostsListingPath(tenant), label: { [Locale.cs]: "Blog", [Locale.en]: "Blog" } },
				{ link: getContactPath(tenant), label: { [Locale.cs]: "Kontakt", [Locale.en]: "Contact" } },
			],
		},
	];

	const [facebookUrl, instagramUrl, youtubeUrl] = fathersContact.socialProfiles;
	const socials: { link: string; label: string; icon: string }[] = [
		{ link: facebookUrl, label: "Facebook", icon: facebookIcon },
		{ link: instagramUrl, label: "Instagram", icon: instagramIcon },
		{ link: youtubeUrl, label: "YouTube", icon: youtubeIcon },
	];

	const columnHeadingClass = tw("text-white text-base md:text-1.5xl/8 font-antipol");
	const labelClass = tw("text-xs/4 uppercase tracking-[.1em] text-white/60");
	const lineClass = tw("text-sm/[2.24] md:text-base/[2.24]");
	const linkClass = tw("text-white hover:text-fcrbrown transition-colors");
	const mutedLinkClass = tw("text-white/60 hover:text-white transition-colors");

	const isDesktop = createReactiveBreakpoint(">md");

	$effect(() => {
		document.body.style.setProperty(
			"--cookiescript-badge-bottom",
			pageHasMobileBottomBar && !isDesktop.current ? "7.5rem" : "unset",
		);
	});
</script>

<footer
	class={[
		"bg-fcrdarkbrown full-width grid-container font-sans font-medium md:pt-20 md:pb-10",
		pageHasMobileBottomBar ? "pt-14 pb-38" : "py-14",
	]}
>
	<div
		class={[
			"grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 md:gap-x-10 md:gap-y-12",
			"lg:grid-cols-[1fr_auto_auto_auto] xl:gap-x-14 2xl:gap-x-20",
		]}
	>
		<section
			class={[
				"col-span-full flex flex-col gap-6 lg:col-span-1",
				"md:flex-row md:flex-wrap md:items-start md:gap-x-14 lg:flex-col",
			]}
		>
			<div class="flex flex-col gap-6">
				<a href={getHomepagePath(tenant)} aria-label={translate("logoAria")} class="w-fit">
					<Logo class="w-33 h-7.5 pr-px text-white" />
				</a>
				<div class={["flex flex-col", lineClass]}>
					<a href="tel:{fathersContact.phone}" class={[linkClass, "w-fit"]}>
						{formatPhoneNumber(fathersContact.phone)}
					</a>
					<a href="mailto:{fathersContact.email}" class={[linkClass, "w-fit"]}>{fathersContact.email}</a>
				</div>
			</div>
			<div class="flex gap-3">
				{#each socials as social (social)}
					<a
						href={social.link}
						target="_blank"
						referrerpolicy="no-referrer"
						rel="noreferrer"
						class={[
							"size-16 border-white/16 hover:border-white transition-colors",
							"flex justify-center items-center rounded-full border",
						]}
						title={social.label}
					>
						<Icon icon={social.icon} class="size-6 text-white" />
					</a>
				{/each}
			</div>
		</section>
		{#each footerMenu as { title, links } (title)}
			<section class="flex flex-col gap-2">
				<h2 class={columnHeadingClass}>
					<Formatted text={title} />
				</h2>
				<nav>
					{#each links as linkItem (linkItem)}
						<a href={linkItem.link} class={["block", lineClass, linkClass]}>
							<Formatted text={linkItem.label[tenant.locale]} />
						</a>
					{/each}
				</nav>
			</section>
		{/each}
		<section class="col-span-full flex flex-col gap-4 md:col-span-1 md:gap-2">
			<h2 class={columnHeadingClass}>
				<Formatted text={translate("places")} />
			</h2>
			<div class="grid grid-cols-2 gap-x-6 gap-y-4 md:flex md:flex-col">
				{#each fathersPlaces as place (place)}
					<div class="flex flex-col gap-1 text-sm/normal md:text-base/normal">
						<a
							href={place.mapUrl}
							target="_blank"
							rel="noreferrer"
							title={translate("map")}
							class={["inline-flex w-fit items-center gap-1 font-semibold", linkClass]}
						>
							<Formatted text={place.name[tenant.locale]} />
							<Icon icon={chevronIcon} aria-hidden="true" class="size-5 -rotate-90" />
						</a>
						<div class="text-white/60">
							{#each place.openingHours as line (line)}
								<div><Formatted text={line[tenant.locale]} /></div>
							{/each}
						</div>
					</div>
				{/each}
			</div>
		</section>
		<section class="col-span-full flex flex-wrap gap-x-8 gap-y-6 md:gap-x-10">
			<div class="flex flex-col gap-3">
				<div class={labelClass}><Formatted text={translate("language")} /></div>
				<LanguageSwitcher />
			</div>
			<div class="flex flex-col gap-3">
				<div class={labelClass}><Formatted text={translate("currency")} /></div>
				<CurrencySwitcher />
			</div>
			<div class="flex flex-col gap-3">
				<div class={labelClass}><Formatted text={translate("delivery")} /></div>
				<DeliveryCountrySelect
					name="deliveryCountry"
					placeholder={translate("deliveryTo")}
					value={selectedDestinationCountry.current ?? defaultDestinationCountry}
					onchange={async (event: Event & { currentTarget: HTMLSelectElement }): Promise<void> => {
						await setCookie(destinationCountryCookieName, event.currentTarget.value as Country);
						location.reload();
					}}
					wrapperClass="text-white min-w-45 max-w-60"
					class="bg-transparent border-white/16 text-white [&::picker(select)]:!border-fcrdarkgray"
				/>
			</div>
		</section>
		<section
			class={[
				"col-span-full border-t border-white/16 pt-8 text-sm/none",
				"flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between",
			]}
		>
			<nav class="flex flex-wrap items-center gap-x-6 gap-y-3">
				<div class="text-white/60">
					<Formatted text={translate("copyright")} />
				</div>
				{#each footerLinks as linkItem (linkItem)}
					<a href={linkItem.link} class={mutedLinkClass}>
						<Formatted text={linkItem.label[tenant.locale]} />
					</a>
				{/each}
			</nav>
			<div class="flex flex-wrap items-center gap-x-6 gap-y-3 xl:shrink-0">
				<div class="text-white/60 shrink-0">
					<Formatted text={translate("credit")} />
				</div>
				<a
					href="https://www.fastest.cz/"
					target="_blank"
					rel="noopener"
					aria-label="Fastest Solution"
					title="Fastest Solution"
				>
					<Icon icon={fastestLogo} class="h-5 w-35 text-white" />
				</a>
			</div>
		</section>
	</div>
</footer>
